/** Shared bits for the application-mail UI: category colours, labels, queries. */

import { useQuery } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { mailApi } from '@/api/endpoints'
import type { Tone } from '@/components/ui/StatusPill'
import type { MailAlias, MailCategory } from '@/types/api'

export interface CategoryMeta {
  label: string
  /** The StatusPill tone for this category. */
  tone: Tone
  /** Soft chip fill + text (token classes only, so both themes work). */
  chip: string
  /** Solid dot. */
  dot: string
}

export const CATEGORY_META: Record<MailCategory, CategoryMeta> = {
  verification: { label: 'Verification', tone: 'info', chip: 'bg-info/12 text-info', dot: 'bg-info' },
  rejection: { label: 'Rejection', tone: 'danger', chip: 'bg-danger/12 text-danger', dot: 'bg-danger' },
  interview: { label: 'Interview', tone: 'brand', chip: 'bg-brand-soft text-brand', dot: 'bg-brand' },
  assessment: { label: 'Assessment', tone: 'warning', chip: 'bg-warning/14 text-warning', dot: 'bg-warning' },
  reminder: { label: 'Reminder', tone: 'neutral', chip: 'bg-accent-soft text-ink-soft', dot: 'bg-ink-soft' },
  offer: { label: 'Offer', tone: 'success', chip: 'bg-success/12 text-success', dot: 'bg-success' },
  applied: { label: 'Applied', tone: 'neutral', chip: 'bg-ink/[0.06] text-ink-soft', dot: 'bg-ink-muted' },
  other: { label: 'Other', tone: 'neutral', chip: 'bg-surface-muted text-ink-muted', dot: 'bg-ink-faint' },
}

export function categoryMeta(category: string | null | undefined): CategoryMeta {
  return CATEGORY_META[(category ?? 'other') as MailCategory] ?? CATEGORY_META.other
}

/** The filter chips, in the order the inbox shows them. */
export const FILTER_CATEGORIES: MailCategory[] = [
  'verification',
  'rejection',
  'interview',
  'assessment',
  'reminder',
  'offer',
  'applied',
]

export const PORTAL_LABEL: Record<string, string> = {
  workday: 'Workday',
  greenhouse: 'Greenhouse',
  lever: 'Lever',
  ashby: 'Ashby',
  smartrecruiters: 'SmartRecruiters',
  icims: 'iCIMS',
  oracle: 'Oracle',
  taleo: 'Taleo',
  successfactors: 'SuccessFactors',
  naukri: 'Naukri',
}

/** True when the endpoint has not shipped yet (so: show an empty state, not an error). */
export function isUnavailable(error: unknown): boolean {
  return error instanceof ApiError && [404, 405, 501].includes(error.status)
}

/** A missing endpoint will not appear on retry: fail fast. */
export function retryUnlessMissing(failureCount: number, error: unknown): boolean {
  if (isUnavailable(error) || (error instanceof ApiError && error.status === 422)) return false
  return failureCount < 2
}

export function useMailAlias() {
  return useQuery({
    queryKey: ['mail-alias'],
    queryFn: mailApi.alias,
    staleTime: 60_000,
    retry: retryUnlessMissing,
  })
}

export function useMailStats(poll = false) {
  return useQuery({
    queryKey: ['mail-stats'],
    queryFn: mailApi.stats,
    staleTime: 15_000,
    retry: retryUnlessMissing,
    refetchInterval: (query) => {
      const error = query.state.error
      if (error instanceof ApiError && error.status === 404) return false
      return poll ? 20_000 : 60_000
    },
  })
}

/** True when the alias can actually receive mail. */
export function aliasReady(alias: MailAlias | undefined): alias is MailAlias & { address: string } {
  return Boolean(alias?.address && alias.domainConfigured)
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}
